/**
 * entityRecordsRegistry actions
 *
 * Imperative writers + subscription management for the registry singleton.
 * Ports the bodies of the former `EntityRecordsRegistryProvider`'s `useCallback`s
 * to module-level functions that mutate the Valtio proxy in `./store`.
 *
 * Demand-driven loading: `subscribe(entityId, groupId?)` increments a refcount
 * and triggers `loadRecords` on the first subscriber. Returns a cleanup that
 * decrements the refcount and schedules a 30s grace teardown of the cache
 * entry when the count reaches zero.
 */
import debounce from 'lodash/debounce';
import { DEFAULT_SEARCH_DEBOUNCE_MS } from '#types/System.Parameters.types';
import type { RecordsCacheKey } from '#types/System.RecordsRegistry.types';
import type { EntityRecord } from '#types/System.types';
import { fetchRecord } from './fetchRecord';
import { fetchRecordsList } from './fetchRecordsList';
import {
    cacheKey,
    entityRecordsRegistryStore,
    gracePeriodTimers,
    lastFetchTimestamps,
    MISS_RETRY_MS,
    pendingSingleFetches,
    pendingUsageRegistrations,
    subscriberCounts,
} from './store';

const INITIAL_LOAD_LIMIT = 1000;
const TEARDOWN_GRACE_MS = 30_000;

const forgetMiss = (key: RecordsCacheKey, recordId: string | undefined): void => {
    const bucket = entityRecordsRegistryStore.misses.get(key);
    if (!recordId || !bucket?.[recordId]) return;
    entityRecordsRegistryStore.misses.set(
        key,
        Object.fromEntries(Object.entries(bucket).filter(([id]) => id !== recordId)),
    );
};

const markMiss = (key: RecordsCacheKey, recordId: string, outcome: 'absent' | 'failed'): void => {
    if (!subscriberCounts.has(key)) return;
    entityRecordsRegistryStore.misses.set(key, {
        ...entityRecordsRegistryStore.misses.get(key),
        [recordId]: { at: Date.now(), outcome },
    });
};

const loadRecords = async (entityId: string, entityGroupId?: string): Promise<void> => {
    const key = cacheKey(entityId, entityGroupId);

    entityRecordsRegistryStore.loading.set(key, true);
    entityRecordsRegistryStore.errors.set(key, null);

    try {
        const records = await fetchRecordsList({
            entityId,
            limit: INITIAL_LOAD_LIMIT,
            entityGroupId,
        });

        entityRecordsRegistryStore.records.set(key, records);
        lastFetchTimestamps.set(key, Date.now());
    } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        const errorCode = (err as unknown as { code?: unknown }).code;
        const isPermissionDenied =
            errorCode === 'permission-denied' || errorCode === 'functions/permission-denied';

        if (isPermissionDenied) {
            entityRecordsRegistryStore.records.set(key, []);
        }

        entityRecordsRegistryStore.errors.set(key, err);

        if (!isPermissionDenied) {
            console.error(`Failed to load records for ${key}:`, error);
        }
    } finally {
        entityRecordsRegistryStore.loading.set(key, false);
    }
};

export const subscribe = (entityId: string, entityGroupId?: string): () => void => {
    const key = cacheKey(entityId, entityGroupId);

    const currentCount = subscriberCounts.get(key) || 0;
    subscriberCounts.set(key, currentCount + 1);

    const pendingTimer = gracePeriodTimers.get(key);
    if (pendingTimer) {
        clearTimeout(pendingTimer);
        gracePeriodTimers.delete(key);
    }

    if (
        !entityRecordsRegistryStore.records.has(key) &&
        !entityRecordsRegistryStore.loading.get(key)
    ) {
        void loadRecords(entityId, entityGroupId);
    }

    return () => {
        const count = subscriberCounts.get(key) || 0;
        const newCount = Math.max(0, count - 1);
        subscriberCounts.set(key, newCount);

        if (newCount > 0) return;
        if (gracePeriodTimers.has(key)) return;

        const timer = setTimeout(() => {
            const stillZero = subscriberCounts.get(key) === 0;
            if (stillZero) {
                entityRecordsRegistryStore.records.delete(key);
                entityRecordsRegistryStore.loading.delete(key);
                entityRecordsRegistryStore.errors.delete(key);
                entityRecordsRegistryStore.misses.delete(key);
                lastFetchTimestamps.delete(key);
                subscriberCounts.delete(key);
            }
            gracePeriodTimers.delete(key);
        }, TEARDOWN_GRACE_MS);

        gracePeriodTimers.set(key, timer);
    };
};

export const addRecordToCache = (
    entityId: string,
    record: EntityRecord,
    entityGroupId?: string,
): void => {
    const key = cacheKey(entityId, entityGroupId);
    const existing = entityRecordsRegistryStore.records.get(key) || [];
    const existingIndex = existing.findIndex((r) => r.docId === record.docId);
    const updated = existingIndex >= 0
        ? existing.map((r, i) => (i === existingIndex ? record : r))
        : [record, ...existing];
    entityRecordsRegistryStore.records.set(key, updated);
    forgetMiss(key, record.docId);
};

export const removeRecordFromCache = (entityId: string, recordId: string): void => {
    const prefix = `${entityId}:`;
    entityRecordsRegistryStore.records.forEach((records, key) => {
        if (!key.startsWith(prefix)) return;
        const filtered = records.filter((r) => r.docId !== recordId);
        if (filtered.length !== records.length) {
            entityRecordsRegistryStore.records.set(key, filtered);
        }
    });
};

export const updateRecordInCache = (entityId: string, recordMetadata: EntityRecord): void => {
    const allGroups = [undefined, ...(recordMetadata.entityGroupIds || [])];
    allGroups.forEach((groupId) => {
        const key = cacheKey(entityId, groupId);
        const existing = entityRecordsRegistryStore.records.get(key) || [];
        const existingIndex = existing.findIndex((r) => r.docId === recordMetadata.docId);
        const updated = existingIndex >= 0
            ? existing.map((r, i) => (i === existingIndex ? recordMetadata : r))
            : [recordMetadata, ...existing];
        entityRecordsRegistryStore.records.set(key, updated);
        forgetMiss(key, recordMetadata.docId);
    });
};

/**
 * Synchronous cache read that heals its own misses: a miss kicks off a
 * background `fetchRecord` whose result lands in the cache and re-renders
 * subscribers. A read that yields no record is marked in the bucket's `misses`
 * (`absent` or `failed`), which a reader consults to tell "does not exist" from
 * "still reading". A mark holds back this bucket's re-read of that id for
 * `MISS_RETRY_MS`; the first call after that re-reads, so a record created since
 * is found within the interval. A mark lands only while the bucket is live, so a
 * read settling after teardown, or for an id no subscriber's bucket holds,
 * leaves nothing behind. `pendingSingleFetches` dedupes only the in-flight window, so it
 * must clear on every settlement — a key left behind permanently suppresses
 * the refetch once the record leaves the bucket (`loadRecords` replacing it,
 * or the grace teardown deleting it).
 */
export const getRecord = (
    entityId: string,
    recordId: string,
    entityGroupId?: string,
): EntityRecord | undefined => {
    const key = cacheKey(entityId, entityGroupId);
    const cached = entityRecordsRegistryStore.records.get(key);
    const found = cached?.find((r) => r.docId === recordId);
    if (found) return found;

    const miss = entityRecordsRegistryStore.misses.get(key)?.[recordId];
    if (miss && Date.now() - miss.at < MISS_RETRY_MS) return undefined;

    const fetchKey = `${entityId}:${recordId}`;
    if (pendingSingleFetches.has(fetchKey)) return undefined;
    pendingSingleFetches.add(fetchKey);

    fetchRecord(entityId, recordId)
        .then((record) => {
            if (record) {
                addRecordToCache(entityId, record, entityGroupId);
                return;
            }
            markMiss(key, recordId, 'absent');
        })
        .catch(() => markMiss(key, recordId, 'failed'))
        .finally(() => {
            pendingSingleFetches.delete(fetchKey);
        });

    return undefined;
};

export const refreshRecords = async (entityId: string, entityGroupId?: string): Promise<void> => {
    await loadRecords(entityId, entityGroupId);
};

const debouncedFlushUsageRegistrations = debounce(() => {
    if (pendingUsageRegistrations.length === 0) return;

    const byEntity = pendingUsageRegistrations.reduce((acc, reg) => {
        if (!acc[reg.entityId]) acc[reg.entityId] = [];
        acc[reg.entityId].push(reg.recordId);
        return acc;
    }, {} as Record<string, string[]>);

    Object.entries(byEntity).forEach(([entityId, recordIds]) => {
        fetchRecordsList({
            entityId,
            registerUsage: recordIds,
        }).catch((err) => console.warn('Common records usage tracking failed:', err));
    });

    pendingUsageRegistrations.length = 0;
}, DEFAULT_SEARCH_DEBOUNCE_MS);

export const registerCommonRecordUsage = (entityId: string, recordId: string): void => {
    pendingUsageRegistrations.push({ entityId, recordId });
    debouncedFlushUsageRegistrations();
};
